/**
 * 読み取り専用の JSON / ログ表示。
 *
 * **ページ本体を絶対に横スクロールさせない**（05-ui §11.1）ため、
 * 横溢れはこのコンポーネントの内側で `overflow-x-auto` に閉じ込める。
 */
import * as React from 'react';
import { Check, Copy, Download } from 'lucide-react';
import { Button, cn } from '@mail-sv/ui';

export function CodeBlock({
  value,
  language,
  maxHeight = '28rem',
  filename,
  className,
}: {
  value: string;
  /** 表示ラベルのみ。シンタックスハイライトは入れない（依存を増やさない）。 */
  language?: string;
  maxHeight?: string;
  /** 指定するとダウンロードボタンを出す。 */
  filename?: string;
  className?: string;
}): React.ReactElement {
  const [copied, setCopied] = React.useState(false);

  const copy = React.useCallback(() => {
    void navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    });
  }, [value]);

  const save = React.useCallback(() => {
    const blob = new Blob([value], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename ?? 'download.txt';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }, [value, filename]);

  return (
    <div className={cn('overflow-hidden rounded-lg border border-border bg-panel', className)}>
      <div className="flex items-center justify-between gap-2 border-b border-border px-3 py-1.5">
        <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
          {language ?? 'text'}
        </span>
        <div className="flex items-center gap-1">
          {filename ? (
            <Button variant="ghost" size="xs" onClick={save} aria-label="ダウンロード">
              <Download aria-hidden />
              保存
            </Button>
          ) : null}
          <Button variant="ghost" size="xs" onClick={copy} aria-label="クリップボードへコピー">
            {copied ? <Check aria-hidden /> : <Copy aria-hidden />}
            {copied ? 'コピーしました' : 'コピー'}
          </Button>
        </div>
      </div>
      <pre className="overflow-x-auto p-3 text-xs leading-relaxed" style={{ maxHeight }}>
        <code className="font-mono text-foreground">{value}</code>
      </pre>
    </div>
  );
}

/** オブジェクトを整形して表示する薄いラッパ。 */
export function JsonBlock({
  value,
  maxHeight,
  filename,
  className,
}: {
  value: unknown;
  maxHeight?: string;
  filename?: string;
  className?: string;
}): React.ReactElement {
  const text = React.useMemo(() => {
    try {
      return JSON.stringify(value, null, 2) ?? 'null';
    } catch {
      return '(表示できない値です)';
    }
  }, [value]);
  return <CodeBlock value={text} language="json" maxHeight={maxHeight} filename={filename} className={className} />;
}
